import { getFieldFillableElements } from "..";

/**
 * @class FormRestructureEntry
 * @classdesc Describes a logical section of the form that has changed within a FormRestructureEvent.
 * @todo Support Repeaters (those will need to be accounted for in "restructure" events. Will need to decide how to represent them. Composite pattern?)
 *
 * @prop {HTMLElement|null} field If non-null, the container element with the "field" class.
 * @prop {HTMLFieldSetElement|null} fieldset If non-null, the <fieldset> element.
 * @prop {HTMLElement|null} page If non-null, the container element for the "page" of fieldsets associated with this step of the form.
 * @prop {HTMLElement|null} orphan If non-null, the entry houses an orphaned element (usually are generated elements from third-party frameworks).
 * @prop {(HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement)[]} elements Getter that produces the set of fillable elements within this entry's content.
 */
export class FormRestructureEntry {

  static fieldSelector = ".field,fieldset.fieldgroup";
  static fieldsetSelector = "fieldset:not(.fieldgroup)";
  static pageSelector = "[data-webform-key]";

  /**
   * @param {HTMLElement} element The element associated with the entry.
   */
  constructor(element) {
    const isSingleField = element.classList.contains("field");
    const isFieldSet = element instanceof HTMLFieldSetElement;
    const isFieldGroup = isFieldSet && element.classList.contains("fieldgroup");

    /** @type {HTMLElement|null} If non-null, the container element with the "field" class. */
    this.field = isSingleField || isFieldGroup ? element : null;

    /** @type {HTMLFieldSetElement|null} If non-null, the <fieldset> element. */
    this.fieldset = !this.field && isFieldSet ? element : null;

    /** @type {HTMLElement|null} If non-null, the container element for the "page" of fieldsets associated with this step of the form. */
    this.page = !this.fieldset && !!element.dataset.webformKey ? element : null;

    /** @type {HTMLElement|null} If non-null, the entry houses an orphaned element (usually are generated elements from third-party frameworks). */
    this.orphan = !this.field && !this.fieldset && !this.page && "form" in element && element.form ? element : null;
  }

  /**
   * @type {(HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement)[]}
   */
  get elements() {
    if (this.field) {
      return getFieldFillableElements(this.field);
    } else if (this.fieldset) {
      return getFieldFillableElements(this.fieldset);
    } else if (this.page) {
      return getFieldFillableElements(this.page);
    } else if (this.orphan) {
      return getFieldFillableElements(this.orphan);
    }
    return [];
  }

  get isEmpty() {
    return this.field == null
      && this.fieldset == null
      && this.page == null
      && this.orphan == null;
  }
}
